import { faPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlightDetails,
  ListOFCatalogPrice,
  Outbound,
} from 'ts/flight/interfaces/FlightDetailsResponse';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import { faFileLines, faMoneyBill1 } from '@fortawesome/free-regular-svg-icons';
import RulesModal from './RulesModal';
import PassengerList from './PassengersList';
import BoundsList from './BoundsList';
import HandleFee from 'components/shared/HandleFee';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import SalesPrice from 'components/shared/SalesPrice';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useSearchParams } from 'react-router-dom';
import { FlProposal } from 'ts/shared/interfaces/CreateProposalParams';
import { MainContext } from 'contexts/MainContext';

interface MultiBookSummaryProps {
  inFlight: FlightDetails;
  outFlight: FlightDetails;
  outbounds: Outbound[];
  inbounds: Outbound[];
  successPage?: boolean;
  addPrice: number;
  outBags: ListOFCatalogPrice[];
  inBags: ListOFCatalogPrice[];
  setCouponCode: (code: string) => void;
  couponCode: string;
  onClickApply: () => void;
}

const MultiBookSummary: FC<MultiBookSummaryProps> = ({
  outbounds,
  inbounds,
  inFlight,
  outFlight,
  successPage,
  ...props
}) => {
  const [t] = useTranslation();
  const [showRulesOut, setShowRulesOut] = useState(false);
  const [showRulesIn, setShowRulesIn] = useState(false);
  const { proposal, setProposal } = useContext(MainContext);
  const [urlParams] = useSearchParams();

  const total =
    outFlight.flighPrice.grossAndTaxWithMarkup.value +
    inFlight.flighPrice.grossAndTaxWithMarkup.value +
    props.addPrice;

  const currency = getCurrency(outFlight.priceCurrency);
  const bagsInPrice = props.inBags?.reduce((acc, bag) => acc + bag.price, 0) || 0;
  const bagsOutPrice = props.outBags?.reduce((acc, bag) => acc + bag.price, 0) || 0;
  const onClickProposal = () => {
    if (!proposal || !setProposal) return;

    const existingOutboundIndex = proposal.flights.findIndex(
      (proposal) => proposal.item.id === outFlight.id
    );
    const existingInboundIndex = proposal.flights.findIndex(
      (proposal) => proposal.item.id === inFlight.id
    );

    if (existingOutboundIndex !== -1 || existingInboundIndex !== -1) {
      const updatedFlights = proposal.flights.filter(
        (_, index) => index !== existingOutboundIndex && index !== existingInboundIndex
      );
      setProposal({ ...proposal, flights: updatedFlights });
    } else {
      const newProposal: FlProposal = {
        item: JSON.parse(JSON.stringify(outFlight)),
        currency: outFlight.flighPrice.grossAndTaxWithMarkup.currency,
        price: outFlight.flighPrice.grossAndTaxWithMarkup.value + bagsOutPrice,
        displayPrice: true,
        searchToken: urlParams.get('searchToken') || '',
        bags: props.outBags,
      };

      const newProposalIn: FlProposal = {
        item: JSON.parse(JSON.stringify(inFlight)),
        currency: inFlight.flighPrice.grossAndTaxWithMarkup.currency,
        price: inFlight.flighPrice.grossAndTaxWithMarkup.value + bagsInPrice,
        displayPrice: true,
        searchToken: urlParams.get('pairSearchToken') || '',
        bags: props.inBags,
      };

      setProposal({ ...proposal, flights: [...proposal.flights, newProposal, newProposalIn] });
    }
  };

  return (
    <div>
      <div
        className={
          'details-summary book-summary default-shadow flight-details-summary position-relative' +
          (successPage ? ' flight-details-summary-success' : '')
        }
      >
        <ProposalHeart
          active={!!proposal?.flights.some((f) => f.item.id === outFlight.id)}
          onClickPropsal={onClickProposal}
        />
        <div className="title-underline text-lg mb-20 title-header-grey">
          {t('passenger.order_details')}
        </div>
        <div className="title text-st text-contrast">
          <FontAwesomeIcon icon={faPlane as any} className="mr-5" size="sm" />
          {t('flight.details')}
        </div>
        <BoundsList bounds={outbounds} />
        <BoundsList bounds={inbounds} />
        {!successPage && (
          <Fragment>
            <div className="fl-pair-ticket-ico mb-10 text-bold text-sm text-center text-lh bg-warning p-510">
              ATTENTION! TWO RESERVATION (SEGMENTS) WILL BE PROVIDED.
            </div>
            <div className="divider mb-20"></div>
            <div className="title text-st text-contrast">
              <FontAwesomeIcon icon={faFileLines as any} className="mr-5" size="sm" />
              {t('flight.info_and_conditions')}
            </div>
            <div className="text-sm mb-20 text-lh-sm">
              <span>{t('flight.it_is_your')}</span>
              <span>{t('flight.all_rules')}</span>
              <span>{t('flight.according_this')} </span>
              <span className="text-contrast text-underline" onClick={() => setShowRulesOut(true)}>
                {t('flight.outbound')}
              </span>
              <span> / </span>
              <span className="text-contrast text-underline" onClick={() => setShowRulesIn(true)}>
                {t('flight.inbound')}
              </span>
              <HandleFee name="Flights" />
            </div>
          </Fragment>
        )}
        <div className="divider mb-20"></div>
        <div className="flex-between mb-10">
          <div className="title text-st text-contrast">
            <FontAwesomeIcon icon={faMoneyBill1 as any} className="mr-5" size="sm" />
            {t('flight.booking_summary')}
          </div>
          <span className="text-bold text-md">{formatPriceCurrency(total, currency)}</span>
        </div>
        {outFlight && <div className="mb-10">{t('flight.outbound')}</div>}
        <PassengerList flight={outFlight} />
        {inFlight && <div className="mb-10">{t('flight.inbound')}</div>}
        <PassengerList flight={inFlight} />
        <div className="mt-20 flex-between text-lg text-bold title-footer-grey">
          <span>{t('flight.total_per_package')}</span>
          <span className="text-contrast">{formatPriceCurrency(total, currency)}</span>
        </div>
        {!successPage && (outFlight.provider === 'LOW' || inFlight.provider === 'LOW') && (
          <div className="fl-pair-ticket-ico mt-15  text-bold text-sm text-center  bg-danger p-510">
            All Low-cost bookings are Non-refundable!!!
          </div>
        )}
        {showRulesOut && (
          <RulesModal
            flightId={outFlight.id}
            onClose={() => setShowRulesOut(false)}
            tokenKey="searchToken"
          />
        )}
        {showRulesIn && (
          <RulesModal
            flightId={inFlight.id}
            onClose={() => setShowRulesIn(false)}
            tokenKey="pairSearchToken"
          />
        )}
      </div>

      {!successPage && (
        
          <SalesPrice
            currency={outFlight.priceCurrency}
            setSaleValue={props.setCouponCode}
            saleValue={props.couponCode}
            price={total}
            onClickApply={props.onClickApply}
          />
        
      )}
    </div>
  );
};

export default withErrorBoundary(MultiBookSummary, 'MultiBookSummary');
